import type FormView from './formView';
// @ts-expect-error: Could not find a declaration file for module
import getResource from '../core/resources';
// @ts-expect-error: Could not find a declaration file for module
import Dialog from '../core/Dialog';
// @ts-expect-error: Could not find a declaration file for module
import MaximazableDialog from '../core/MaximazableDialog';
import { wire } from '../../vendors/hyperhtml';
// @ts-expect-error: Could not find a declaration file for module
import SvgManager from '../core/SvgManager';
// @ts-expect-error: Could not find a declaration file for module
import multiLingualDialog from '../aras/dialogs/multiLingual';
// @ts-expect-error: Could not find a declaration file for module
import processKeyboardEvent from '../core/keyboardEvent';

const icons = {
	multiLingual: '../images/InputMultiLingual.svg'
};

SvgManager.enqueue(Object.values(icons));

export interface BasePropertyMetadata {
	type: string;
	label: string;
	cssClass?: string;
	disabled?: boolean;
	required?: boolean;
}

interface ItemPropertyMetadata extends BasePropertyMetadata {
	itemTypeName: string;
}

interface MultiLingualPropertyMetadata extends BasePropertyMetadata {
	defaultLanguageCode: string;
	languages: Array<{ code: string; name: string }>;
}

const setIcon = async (propName: string, control: FormView) => {
	// @ts-expect-error: aras does not exist on type 'Window'
	const dialogParameters = { type: 'ImageBrowser', aras: window.aras };
	const dialog = new Dialog('iframe', dialogParameters);
	dialog.show();
	let iconPath = await dialog.promise;
	if (!iconPath) {
		return;
	}

	if (iconPath === 'set_nothing') {
		iconPath = '';
	}

	changeData(propName, iconPath, control);
};

const setItem = async (propName: string, control: FormView, event: Event) => {
	// @ts-expect-error: Property 'state' does not exist on type 'EventTarget'
	const itemPropertyComponentState = event.currentTarget?.state;
	if (!itemPropertyComponentState) {
		return;
	}

	const selectedLabel = itemPropertyComponentState.label;
	const item = itemPropertyComponentState.list.find(
		(item: Record<string, string>) => item.label === selectedLabel
	);
	if (!item) {
		return;
	}

	const newPropValue = {
		label: selectedLabel,
		itemId: item.itemId
	};
	changeData(propName, newPropValue, control);
};

const showItemSearchDialog = async (
	propName: string,
	propMetadata: ItemPropertyMetadata,
	control: FormView
) => {
	const dialogParams = {
		itemtypeName: propMetadata.itemTypeName,
		type: 'SearchDialog'
	};
	const dialog = new MaximazableDialog('iframe', dialogParams);
	dialog.show();
	const dialogResult = await dialog.promise;
	if (!dialogResult || !dialogResult.itemID) {
		return;
	}

	const newPropValue = {
		itemId: dialogResult.itemID,
		label: dialogResult.keyed_name
	};
	changeData(propName, newPropValue, control);
};

const updateMultilingualState = (
	propName: string,
	metadata: MultiLingualPropertyMetadata,
	value: Record<string, string>,
	control: FormView
) => {
	const { defaultLanguageCode } = metadata;
	const defaultLanguageValue = value[defaultLanguageCode];
	const invalid = !defaultLanguageValue;
	const propertyState = control.state[propName] || {};
	const newPropertyState = {
		...propertyState,
		invalid
	};
	control.setState({ [propName]: newPropertyState });
};

const setMultiLingual = (
	propName: string,
	metadata: MultiLingualPropertyMetadata,
	value: undefined | Record<string, string>,
	valueChanges: Record<string, string>,
	control: FormView
) => {
	const newValue = {
		...(value || {}),
		...valueChanges
	};
	const { required } = metadata;
	if (required) {
		updateMultilingualState(propName, metadata, newValue, control);
	}
	changeData(propName, newValue, control);
};

const showMultiLingualDialog = async (
	propName: string,
	value: undefined | Record<string, string>,
	metadata: MultiLingualPropertyMetadata,
	control: FormView
) => {
	const translations = metadata.languages.reduce(
		(
			result: Array<{
				code: string;
				name: string;
				value: undefined | string;
			}>,
			language
		) => {
			const languageValue = value?.[language.code];
			result.push({
				...language,
				value: languageValue
			});

			return result;
		},
		[]
	);
	const options = {
		readOnly: metadata.disabled
	};
	const updatedLanguages = (await multiLingualDialog(translations, options)) as
		| undefined
		| Array<{ code: string; value: string }>;
	if (!updatedLanguages || !updatedLanguages.length) {
		return;
	}

	const valueChanges = updatedLanguages.reduce(
		(resultObj: Record<string, string>, { code, value }) => {
			resultObj[code] = value;
			return resultObj;
		},
		{}
	);

	setMultiLingual(propName, metadata, value, valueChanges, control);
};

const changeData = (propName: string, newValue: unknown, control: FormView) => {
	const oldValue = control.data[propName];
	const detail = {
		propName,
		oldValue,
		newValue
	};
	const event = new CustomEvent('change', { detail });
	control.dispatchEvent(event);
};

const createFieldLabel = (
	property: string,
	metadata: BasePropertyMetadata,
	control: FormView
) => {
	const { label, type } = metadata;
	const context = wire(control, `:${property}:${type}:label`);
	return context`
		<span class="aras-form-view__label">
			${label}
		</span>
	`;
};

const createField = (
	property: string,
	value: HTMLElement,
	metadata: BasePropertyMetadata,
	control: FormView
) => {
	const { cssClass = '', type } = metadata;
	const context = wire(control, `:${property}:${type}:field`);
	const labelNode = createFieldLabel(property, metadata, control);
	const content = [labelNode, value];
	const className = `aras-form-view__item ${cssClass}`;
	if (type === 'image') {
		return context`<div class="${className}">${content}</div>`;
	}
	return context`<label class="${className}">${content}</div>`;
};

export const formViewFormatters: Record<
	string,
	(
		propName: string,
		propValue: unknown,
		propMetadata: BasePropertyMetadata,
		control: FormView
	) => HTMLElement
> = {
	image: (propName, iconPath, metadata, control) => {
		const { disabled } = metadata;
		const selectImageLabel = getResource('ui_methods_ex.select_an_image');
		const context = wire(control, `:${propName}:image:value`);
		const img = SvgManager.createHyperHTMLNode(iconPath, {
			class: 'aras-form-view__icon'
		});

		const linkDisabledClass = disabled ? 'aras-link_a-disabled' : '';
		const onclick = disabled ? null : () => setIcon(propName, control);
		const valueNode = context`
			<span
				class="aras-link_a ${linkDisabledClass}"
				onclick="${onclick}">
					${selectImageLabel}
			</span>
			${img}
		`;
		return createField(propName, valueNode, metadata, control);
	},
	item: function (propName, propValue, propMetadata, control) {
		const itemPropertyMetadata = propMetadata as ItemPropertyMetadata;
		const fieldValue = (propValue as Record<string, unknown>)?.label ?? '';
		const disabled = propMetadata.disabled ?? false;
		const required = propMetadata.required ?? false;
		const context = wire(control, `:${propName}:item:value`);
		const valueNode = context`
			<aras-item-property
				class="aras-form-view__item-property"
				mode="field-a"
				itemType="${itemPropertyMetadata.itemTypeName}"
				value="${fieldValue}"
				disabled=${disabled}
				required=${required}
				onchange=${(e: Event) => setItem(propName, control, e)}
				showDialogHandler=${() =>
					showItemSearchDialog(propName, itemPropertyMetadata, control)}
			/>
		`;
		return createField(propName, valueNode, propMetadata, control);
	},
	multiLingual: (propName, propValue, propMetadata, control) => {
		const value = propValue as Record<string, string>;
		const metadata = propMetadata as MultiLingualPropertyMetadata;
		const { languages, defaultLanguageCode, disabled, required } = metadata;
		const { invalid = false } = control.state[propName] || {};
		const inputValue = value?.[defaultLanguageCode];
		const baseClass = 'aras-form-view__multilingual-input';
		let inputButton = null;
		let keydownHandler = null;

		if (languages.length > 1) {
			const inputIcon = SvgManager.createHyperHTMLNode(icons.multiLingual, {
				class: `${baseClass}-button-icon`
			});
			inputButton = wire(control, `:${propName}:multiLingual:input-button`)`
			<button class="${baseClass + '-button'}" onclick="${() => {
				showMultiLingualDialog(propName, value, metadata, control);
			}}">
				${inputIcon}
			</button>`;
			keydownHandler = (e: KeyboardEvent) => {
				const { shortcut } = processKeyboardEvent(e);
				if (shortcut === 'f2') {
					showMultiLingualDialog(propName, value, metadata, control);
				}
			};
		}

		const inputListOfClasses = ['aras-input', baseClass];
		if (invalid) {
			inputListOfClasses.push('aras-input_invalid');
		}
		if (inputButton) {
			inputListOfClasses.push(`${baseClass}_with-button`);
		}
		const inputClass = inputListOfClasses.join(' ');
		const onblur = required
			? (e: FocusEvent) => {
					const input = e.target as HTMLInputElement;
					const inputValue = input.value;
					const valueChanges = {
						[defaultLanguageCode]: inputValue
					};
					updateMultilingualState(propName, metadata, valueChanges, control);
			  }
			: null;
		const context = wire(control, `:${propName}:multiLingual:value`);
		const valueNode = context`
			<div
				class="${baseClass + '-container'} aras-tooltip"
				data-tooltip="${required ? getResource('common.required') : null}"
				data-tooltip-pos="right"
				data-tooltip-show="${invalid}"
			>
				<input
					value="${inputValue}"
					class="${inputClass}"
					disabled="${disabled}"
					onblur="${onblur}"
					onchange="${(e: Event) => {
						const input = e.target as HTMLInputElement;
						const inputValue = input.value;
						const valueChanges = {
							[defaultLanguageCode]: inputValue
						};
						setMultiLingual(propName, metadata, value, valueChanges, control);
					}}"
					onkeydown="${keydownHandler}"
				/>
				${inputButton}
			</div>
		`;
		return createField(propName, valueNode, propMetadata, control);
	}
};
